import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faBuilding, 
  faShieldAlt, 
  faUsers, 
  faCheckCircle, 
  faExclamationTriangle,
  faSync,
  faCrown
} from '@fortawesome/free-solid-svg-icons'
import { useCompany } from '@/frontend/contexts/CompanyContext'
import { useAuth } from '@/frontend/contexts/AuthContext'
import { auth } from '@/frontend/lib/firebase'
import type { CompanyAccess } from '@/frontend/services/invitationService'

// API configuration for company logos
const LOGO_API_BASE = import.meta.env.DEV
  ? 'https://dev.api.dataslot.app'
  : 'https://api.dataslot.app'

export default function CompanySelection() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const { 
    availableCompanies, 
    selectedCompany, 
    loading, 
    error, 
    selectCompany, 
    refreshCompanies,
    hasCompanyAccess,
    isLoaded 
  } = useCompany()

  // Check if user is in switching mode
  const isSwitchingMode = searchParams.get('switching') === 'true'

  // Redirect if user already has a company selected and is not switching
  useEffect(() => {
    if (selectedCompany && isLoaded && !isSwitchingMode) {
      navigate('/', { replace: true })
    }
  }, [selectedCompany, isLoaded, isSwitchingMode, navigate])

  const handleCompanySelect = (company: CompanyAccess) => {
    selectCompany(company)
    navigate('/', { replace: true })
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      console.log('[Auth] User signed out successfully')
    } catch (error) {
      console.error('[Auth] Sign out error:', error)
    }
  }

  const getRoleBadge = (role: string) => {
    if (role === 'SuperAdmin') {
      return <Badge className="bg-amber-100 text-amber-800">
        <FontAwesomeIcon icon={faCrown} className="w-3 h-3 mr-1" />
        SuperAdmin
      </Badge>
    } else if (role === 'Admin') {
      return <Badge className="bg-purple-100 text-purple-800">
        <FontAwesomeIcon icon={faShieldAlt} className="w-3 h-3 mr-1" />
        Admin
      </Badge>
    } else {
      return <Badge className="bg-blue-100 text-blue-800">
        <FontAwesomeIcon icon={faUsers} className="w-3 h-3 mr-1" />
        {role}
      </Badge>
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800">
          <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 mr-1" />
          Active
        </Badge>
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>
    }
  }

  if (loading && !isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600">Loading your companies...</p>
        </div>
      </div>
    )
  }

  if (!hasCompanyAccess && isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full p-6">
          <Alert>
            <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-4">
                <p>You don't have access to any companies in this system.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={refreshCompanies}>
                    <FontAwesomeIcon icon={faSync} className="w-4 h-4 mr-2" />
                    Retry
                  </Button>
                  <Button onClick={handleSignOut}>
                    Sign Out
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faBuilding} className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isSwitchingMode ? 'Switch Company' : 'Select Company'}
            </h1>
            <p className="text-gray-600">
              {isSwitchingMode 
                ? `Currently using ${selectedCompany?.company}. Select a different company to switch.`
                : `Welcome back, ${user?.displayName || user?.email}! Choose which company you'd like to access.`
              }
            </p>
          </div>

          {error && (
            <div className="mb-6">
              <Alert>
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex justify-between items-center">
                    <span>{error}</span>
                    <Button variant="outline" size="sm" onClick={refreshCompanies}>
                      <FontAwesomeIcon icon={faSync} className="w-4 h-4 mr-1" />
                      Retry
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Company Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableCompanies.map((company) => {
              const isCurrentlySelected = selectedCompany?.company === company.company && 
                                         selectedCompany?.module === company.module
              return (
                <Card 
                  key={`${company.company}-${company.module}`}
                  className={`cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 ${
                    isCurrentlySelected && isSwitchingMode ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => handleCompanySelect(company)}
                >
                <CardHeader className="text-center pb-4">
                  <div className="w-12 h-12 mx-auto mb-3 relative">
                    <img 
                      src={`${LOGO_API_BASE}/wfmUtils/s3/object?objectKey=${company.company}%2Fwfm-images%2Flogo`}
                      alt={`${company.company} logo`}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to building icon on error
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLDivElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center hidden">
                      <FontAwesomeIcon icon={faBuilding} className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-xl">{company.company}</CardTitle>
                  <p className="text-sm text-gray-600">{company.module} Module</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Your Role</span>
                    {getRoleBadge(company.role)}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status</span>
                    {getStatusBadge(company.status)}
                  </div>
                  <Button className="w-full" onClick={() => handleCompanySelect(company)}>
                    Access {company.company}
                  </Button>
                  {isCurrentlySelected && isSwitchingMode && (
                    <div className="text-xs text-blue-600 text-center mt-2">Currently Selected</div>
                  )}
                </CardContent>
              </Card>
              )
            })}
          </div>

          {/* Footer */}
          <div className="text-center mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">
              Need access to a different company? Contact your system administrator.
            </p>
            <Button variant="outline" onClick={refreshCompanies} disabled={loading}>
              <FontAwesomeIcon icon={faSync} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Companies
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}